import { isSmsSessionPolicyNumber } from '@provision/conversation-types';
import { SelectMenuOption } from 'src/components/SelectMenu';

// A form-only cap: the shared parser keeps the int4 bound so stored values still load.
const IDLE_TIMEOUT_MAX_SECONDS = 7 * 86_400;

export enum IdleTimeoutUnit {
  Seconds = 'seconds',
  Minutes = 'minutes',
  Hours = 'hours',
}

const UNIT_SECONDS: Record<IdleTimeoutUnit, number> = {
  [IdleTimeoutUnit.Seconds]: 1,
  [IdleTimeoutUnit.Minutes]: 60,
  [IdleTimeoutUnit.Hours]: 3600,
};

export const IDLE_TIMEOUT_UNIT_OPTIONS: SelectMenuOption[] = Object.values(
  IdleTimeoutUnit,
).map((unit) => ({ value: unit, title: unit }));

/** The stored timeout in the largest unit that divides it evenly: 86400 shows as 24 hours. */
export function toIdleTimeoutInput(idleSeconds: number): {
  amount: number;
  unit: IdleTimeoutUnit;
} {
  const unit = largestEvenUnit(idleSeconds);
  return { amount: idleSeconds / UNIT_SECONDS[unit], unit };
}

export function toIdleSeconds(amountText: string, unit: IdleTimeoutUnit): number | null {
  const amount = parsePolicyNumber(amountText);
  if (amount === null) {
    return null;
  }

  const seconds = amount * UNIT_SECONDS[unit];

  return seconds <= IDLE_TIMEOUT_MAX_SECONDS ? seconds : null;
}

export function maxIdleTimeoutAmount(unit: IdleTimeoutUnit): number {
  return Math.floor(IDLE_TIMEOUT_MAX_SECONDS / UNIT_SECONDS[unit]);
}

export function parsePolicyNumber(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) {
    return null;
  }
  const value = Number(trimmed);
  return isSmsSessionPolicyNumber(value) ? value : null;
}

function largestEvenUnit(idleSeconds: number): IdleTimeoutUnit {
  // Every unit divides 0; the design shows it in minutes.
  if (idleSeconds === 0) {
    return IdleTimeoutUnit.Minutes;
  }
  if (idleSeconds % UNIT_SECONDS[IdleTimeoutUnit.Hours] === 0) {
    return IdleTimeoutUnit.Hours;
  }
  if (idleSeconds % UNIT_SECONDS[IdleTimeoutUnit.Minutes] === 0) {
    return IdleTimeoutUnit.Minutes;
  }
  return IdleTimeoutUnit.Seconds;
}
